/**
 * Profile / Account & Billing page  (/profile)
 *
 * Reached by clicking the user-name area in the header.
 *
 * Sections:
 *   1. Profile — editable first / last name.
 *      Email is READ-ONLY here. Changing an email requires Clerk's
 *      verified-email flow; the "Manage email & security" button opens
 *      Clerk's built-in account-management modal which handles that safely.
 *   2. Your apps — per-app plan, billing status, user count,
 *      upgrade / change plan, payment history.
 *   3. Session — Log Out.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { useApi } from '../lib/api';
import { paiseToINR, isPlanCurrent, calculateGstBreakdown } from '../lib/billingHelpers';
import '../styles/Profile.css';

// Sane upper bound for the seat-count input — without it the field accepts
// an arbitrarily long number, which breaks the "Total" price display and
// can overflow validation downstream.
const MAX_SEAT_COUNT = 100000;

// ── Razorpay checkout loader ──────────────────────────────────────────────────
const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
    if (existing) {
      const tick = () => (window.Razorpay ? resolve(true) : setTimeout(tick, 60));
      return tick();
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function formatDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s.includes('T') || s.includes(' ') ? s : `${s}T00:00:00`);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
}

// ── Small presentational bits ─────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cls = {
    active:     'badge-success',
    trial:      'badge-info',
    past_due:   'badge-error',
    suspended:  'badge-error',
    cancelled:  'badge-muted',
    created:    'badge-info',
    captured:   'badge-success',
    success:    'badge-success',
    reconciled: 'badge-success',
    pending:    'badge-warning',
    attempted:  'badge-warning',
    failed:     'badge-error',
    disputed:   'badge-error',
    refunded:   'badge-muted',
  }[status] || 'badge-error';
  const label = {
    captured:   'Success',
    success:    'Success',
    reconciled: 'Success',
    active:     'Active',
    trial:      'Trial',
    past_due:   'Past Due',
    suspended:  'Suspended',
    cancelled:  'Cancelled',
    created:    'Created',
    pending:    'Pending',
    attempted:  'Attempted',
    failed:     'Failed',
    disputed:   'Disputed',
    refunded:   'Refunded',
  }[status] || 'Failed';
  return <span className={`profile-badge ${cls}`}>{label}</span>;
}

// A checkout that's still 'created'/'pending' long after it started was
// abandoned (popup closed, tab closed, etc.) rather than genuinely still in
// progress — display it as failed instead of leaving it looking stuck.
const STALE_PENDING_MS = 15 * 60 * 1000;
function displayPaymentStatus(p) {
  if (p.status === 'created' && p.internal_status === 'pending') {
    const ageMs = Date.now() - new Date(p.created_at).getTime();
    if (ageMs > STALE_PENDING_MS) return 'failed';
  }
  return p.internal_status;
}

function RoleBadge({ role, isOwner }) {
  return (
    <span className="profile-role-badge">
      {isOwner ? 'Owner · ' : ''}{role?.replace('_', ' ')}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Profile() {
  const { user } = useUser();
  const clerk = useClerk();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const api = useApi();

  // ── billing data ────────────────────────────────────────────────
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [info, setInfo]       = useState(null);

  // ── profile editing ─────────────────────────────────────────────
  const [editing, setEditing]   = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '' });
  const [saving, setSaving]     = useState(false);

  // ── modals ───────────────────────────────────────────────────────
  const [upgradeFor, setUpgradeFor]     = useState(null);
  const [upgradeError, setUpgradeError] = useState(null); // inline banner shown inside the upgrade-plan modal itself
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null); // which plan's "Pay & switch" button is actually mid-checkout
  const [paymentsFor, setPaymentsFor]   = useState(null);
  const [paymentsList, setPaymentsList] = useState([]);
  const [seatCountFor, setSeatCountFor] = useState(null); // { app, plan } pending seat-count entry before checkout
  const [seatCountValue, setSeatCountValue] = useState('');

  // ── partnership (active partner, if any) ───────────────────────────
  const [activePartner, setActivePartner] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(true);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [removing, setRemoving] = useState(false);

  // ── load billing summary ─────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setError(null);
      const r = await api.meBillingSummary();
      setData(r);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // ── load active partner ─────────────────────────────────────────
  const loadPartner = useCallback(async () => {
    try {
      const r = await api.getMyPartner();
      setActivePartner(r.active || null);
    } catch (e) {
      // non-fatal — partnership card just won't show
    } finally {
      setPartnerLoading(false);
    }
  }, [api]);

  useEffect(() => { loadPartner(); }, [loadPartner]);

  const handleRemovePartner = async () => {
    if (!removeReason.trim()) return;
    setRemoving(true);
    try {
      await api.removeMyPartner(removeReason.trim());
      setActivePartner(null);
      setRemoveOpen(false);
      setRemoveReason('');
      setInfo('Partnership removed.');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setRemoving(false);
    }
  };

  // ── sign out ─────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  // ── profile edit helpers ─────────────────────────────────────────
  const startEdit = () => {
    setEditForm({
      firstName: user?.firstName || '',
      lastName:  user?.lastName  || '',
    });
    setEditing(true);
    setError(null);
    setInfo(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const saveProfile = async () => {
    const first = editForm.firstName.trim();
    const last  = editForm.lastName.trim();
    if (!first) {
      setError('First name cannot be empty.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // 1. Update Clerk — this is the identity source of truth.
      //    The JWT will carry the new name on the next token refresh, and
      //    syncUser will propagate it to our DB automatically.
      await user.update({ firstName: first, lastName: last });

      // 2. Immediately sync to local DB so the UI reflects the change
      //    without waiting for the next JWT rotation.
      await api.updateMe({ firstName: first, lastName: last });

      setEditing(false);
      setInfo('Profile updated successfully.');
    } catch (e) {
      // Clerk SDK errors surface as e.errors[0].longMessage / .message
      const clerkMsg = e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message;
      const apiMsg   = e?.response?.data?.error;
      setError(clerkMsg || apiMsg || e.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  // ── checkout helper ───────────────────────────────────────────────
  const startCheckout = async (app, plan, memberCount) => {
    if (isPlanCurrent(app.plan, plan)) {
      setUpgradeError('You are already on this plan.');
      return;
    }
    setCheckoutBusy(true);
    setCheckoutPlanId(plan.id);
    setError(null);
    setInfo(null);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        setError('Could not load Razorpay checkout. Check your network and try again.');
        return;
      }
      const resp = await api.billingCheckout(app.app_id, {
        plan_id: plan.id,
        period_months: 1,
        member_count: memberCount,
      });
      const rzp = new window.Razorpay({
        key:         resp.keyId,
        order_id:    resp.orderId,
        amount:      resp.amount,
        currency:    resp.currency,
        name:        resp.appName,
        description: `${plan.name} — ${app.app_name}`,
        prefill: {
          email: user?.emailAddresses?.[0]?.emailAddress,
          name:  user?.firstName
            ? `${user.firstName} ${user.lastName || ''}`.trim()
            : undefined,
        },
        // No `notes` here on purpose — order_id already points at the order
        // created server-side with the full note set (plan_id, member_count,
        // period_months, ...). Re-passing a partial one here would attach a
        // separate, incomplete notes object to the payment entity itself,
        // which is what backend activation reads if it processes the
        // payment.captured webhook before /billing/verify.
        theme: { color: '#1a5276' },
        handler: async (r) => {
          try {
            await api.billingVerify(app.app_id, {
              razorpay_order_id:  r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature:  r.razorpay_signature,
            });
          } catch (verErr) {
            // verify is a fast-path; webhook will reconcile regardless
            console.warn('Verify call failed (webhook will still process):', verErr);
          }
          setInfo('Payment received. Activating your new plan…');
          // Webhook-based activation can take a few seconds — poll billing
          // summary and keep the modal's data in sync so the "Current plan"
          // badge lands on the right card instead of showing stale data.
          for (let attempt = 0; attempt < 8; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 1500));
            try {
              const fresh = await api.meBillingSummary();
              setData(fresh);
              const updated = fresh.items?.find((it) => it.app_id === app.app_id);
              if (updated?.plan?.id === plan.id) {
                setUpgradeFor(updated);
                setInfo('Payment received. Your plan is now active.');
                return;
              }
            } catch (_) { /* keep polling */ }
          }
          setInfo('Payment received. Your plan will activate shortly — reopen this dialog in a moment to confirm.');
          setUpgradeFor(null);
        },
        modal: {
          ondismiss: () => {
            api.billingAbandon(app.app_id, { paymentId: resp.paymentId }).catch(() => {});
          },
        },
      });
      rzp.open();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
        e?.response?.data?.hint  ||
        e.message || 'Checkout failed',
      );
    } finally {
      setCheckoutBusy(false);
      setCheckoutPlanId(null);
    }
  };

  // ── payment history helper ────────────────────────────────────────
  const showPayments = async (app) => {
    setPaymentsFor(app);
    setPaymentsList([]);
    try {
      const r = await api.billingPayments(app.app_id);
      setPaymentsList(r.payments || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  const displayName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : user?.emailAddresses?.[0]?.emailAddress?.split('@')[0];
  const email = user?.emailAddresses?.[0]?.emailAddress;

  // ── render ────────────────────────────────────────────────────────
  return (
    <>
      <Header />
      <main className="profile-page">
        <div className="profile-container">
          <h1 className="profile-title">Account &amp; Billing</h1>

          {/* ── 1. Profile ── */}
          <section className="profile-card">
            <div className="profile-section-header">
              <h2 className="profile-section-title" style={{ marginBottom: 0 }}>Profile</h2>
              {!editing && (
                <button className="btn-secondary" onClick={startEdit}>Edit</button>
              )}
            </div>

            {editing ? (
              <form
                className="profile-edit-form"
                onSubmit={(e) => { e.preventDefault(); saveProfile(); }}
              >
                <div className="profile-field-row">
                  <div className="profile-field">
                    <label htmlFor="prof-first">First name</label>
                    <input
                      id="prof-first"
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm(f => ({ ...f, firstName: e.target.value }))}
                      maxLength={128}
                      autoFocus
                      placeholder="First name"
                    />
                  </div>
                  <div className="profile-field">
                    <label htmlFor="prof-last">Last name</label>
                    <input
                      id="prof-last"
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm(f => ({ ...f, lastName: e.target.value }))}
                      maxLength={128}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                {/* Email — always read-only */}
                <div className="profile-field profile-email-field">
                  <label>Email address</label>
                  <div className="profile-email-row">
                    <input
                      type="email"
                      value={email}
                      readOnly
                      className="profile-input-readonly"
                      tabIndex={-1}
                    />
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => clerk.openUserProfile()}
                    >
                      Change Email →
                    </button>
                  </div>
                  <span className="profile-field-hint">
                    Email changes require identity verification. Use the link above to manage your email through your secure account settings.
                  </span>
                </div>

                <div className="profile-edit-actions">
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="profile-id-row">
                <div>
                  <div className="profile-name">{displayName}</div>
                  <div className="profile-email">{email}</div>
                  <button
                    className="btn-link profile-email-manage"
                    onClick={() => clerk.openUserProfile()}
                    title="Manage email, password and connected accounts"
                  >
                    Manage Email &amp; Security →
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ── Your Partner (only if an active partnership exists) ── */}
          {!partnerLoading && activePartner && (
            <section className="profile-card">
              <div className="profile-section-header">
                <h2 className="profile-section-title" style={{ marginBottom: 0 }}>Your Partner</h2>
              </div>
              <div className="profile-id-row">
                <div>
                  <div className="profile-name">{activePartner.partner_name}</div>
                  <div className="profile-email">Active partnership</div>
                </div>
                {!removeOpen && (
                  <button className="btn-secondary" onClick={() => setRemoveOpen(true)}>
                    Manage Partnership
                  </button>
                )}
              </div>

              {removeOpen && (
                <div className="profile-edit-form" style={{ marginTop: 16 }}>
                  <div className="profile-field">
                    <label htmlFor="remove-partner-reason">Reason for removing this partnership <span style={{ color: 'var(--status-danger-fg)' }}>*</span></label>
                    <textarea
                      id="remove-partner-reason"
                      rows={3}
                      maxLength={500}
                      value={removeReason}
                      onChange={(e) => setRemoveReason(e.target.value)}
                      placeholder="Let us know why you're removing this partner…"
                    />
                  </div>
                  <div className="profile-edit-actions">
                    <button
                      className="btn-primary"
                      onClick={handleRemovePartner}
                      disabled={removing || !removeReason.trim()}
                    >
                      {removing ? 'Removing…' : 'Remove Partner'}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => { setRemoveOpen(false); setRemoveReason(''); }}
                      disabled={removing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── 2. Your apps ── */}
          <section className="profile-card">
            <h2 className="profile-section-title">My Apps</h2>
            {!data && !error && <div className="profile-empty">Loading…</div>}
            {data?.items?.length === 0 && (
              <div className="profile-empty">
                You don't belong to any apps yet.{' '}
                <Link to="/dashboard">Go to Dashboard</Link>.
              </div>
            )}
            <div className="profile-apps-list">
              {data?.items?.map((it) => {
                const overLimit =
                  it.user_limit != null &&
                  it.user_count + it.pending_invites > it.user_limit;
                const planInactive = it.plan != null && !it.plan.is_active;
                return (
                  <div key={it.app_id} className="profile-app-card">
                    <div className="profile-app-header">
                      <div>
                        <h3 className="profile-app-name">{it.app_name}</h3>
                        <div className="profile-app-meta">
                          <RoleBadge role={it.role} isOwner={it.is_owner} />
                          <StatusBadge status={it.billing_status} />
                          {it.access_state === 'GRACE' && (
                            <span className="profile-badge badge-warning">Grace Period</span>
                          )}
                          {it.access_state === 'MANUAL_BLOCKED' && (
                            <span className="profile-badge badge-error">Blocked</span>
                          )}
                          {overLimit && (
                            <span className="profile-badge badge-error">Over User Limit</span>
                          )}
                          {planInactive && it.billing_status !== 'suspended' && (
                            <span className="profile-badge badge-error">Plan Deactivated</span>
                          )}
                        </div>
                      </div>
                      {it.access_state === 'ACTIVE' || it.access_state === 'GRACE' ? (
                        <Link to={`/apps/${it.app_id}/home`} className="btn-secondary">
                          Open App →
                        </Link>
                      ) : null}
                    </div>

                    {it.access_state === 'GRACE' && (
                      <div className="profile-banner warning">
                        Your subscription ended on {formatDate(it.subscription?.period_end)}. You have until{' '}
                        {formatDate(it.grace_expires_at)} to renew before access is blocked.
                      </div>
                    )}
                    {it.access_state === 'MANUAL_BLOCKED' && (
                      <div className="profile-banner error" style={{ marginBottom: 12 }}>
                        This app has been blocked by the platform administrator
                        {it.access_reason ? `: ${it.access_reason}` : '.'} Contact support for assistance.
                      </div>
                    )}

                    <div className="profile-grid">
                      <div className="profile-stat">
                        <div className="profile-stat-label">Current Plan</div>
                        <div className="profile-stat-value">
                          {it.plan?.name || 'No plan'}
                          {it.plan && (
                            <>
                              <div className="profile-stat-sub">
                                {paiseToINR(it.plan.price_paise)} / {it.plan.interval} per user
                              </div>
                              {it.subscription?.total_amount_paise != null ? (
                                <div className="profile-stat-sub">
                                  Last charged: {paiseToINR(it.subscription.base_amount_paise)}
                                  {it.subscription.gst_percentage > 0 && (
                                    <> + {paiseToINR(it.subscription.gst_amount_paise)} GST ({it.subscription.gst_percentage}%)</>
                                  )}
                                  {' '}= {paiseToINR(it.subscription.total_amount_paise)}
                                </div>
                              ) : (
                                <div className="profile-stat-sub">
                                  Total: {paiseToINR(it.plan.price_paise * (it.user_count || 0))}
                                  {it.plan.gst_percentage > 0 && ` + ${it.plan.gst_percentage}% GST`}
                                  {' '}({it.user_count || 0} user{(it.user_count || 0) === 1 ? '' : 's'})
                                </div>
                              )}
                              {it.plan.is_active === 0 && (
                                <div className="profile-stat-sub" style={{ color: '#ef4444', fontWeight: 600 }}>
                                  ⚠ This plan has been discontinued — please switch to an active plan
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="profile-stat">
                        <div className="profile-stat-label">Users</div>
                        <div className="profile-stat-value">
                          {it.user_count}
                          {it.user_limit != null ? ` / ${it.user_limit}` : ' / unlimited'}
                          {it.pending_invites > 0 && (
                            <div className="profile-stat-sub">
                              {it.pending_invites} pending invite{it.pending_invites === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                      </div>
                      {it.billing_status === 'trial' && it.trial_ends_at && (
                        <div className="profile-stat">
                          <div className="profile-stat-label">Trial Ends</div>
                          <div className="profile-stat-value">{formatDate(it.trial_ends_at)}</div>
                        </div>
                      )}
                      {it.subscription?.period_end && (
                        <div className="profile-stat">
                          <div className="profile-stat-label">Renews On</div>
                          <div className="profile-stat-value">{formatDate(it.subscription.period_end)}</div>
                        </div>
                      )}
                    </div>

                    <div className="profile-actions">
                      {it.role === 'super_admin' ? (
                        <>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setUpgradeError(null);
                              setUpgradeFor(it);
                            }}
                          >
                            {it.plan?.code === 'free' || it.billing_status === 'trial'
                              ? 'Upgrade plan'
                              : 'Change plan'}
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => showPayments(it)}
                          >
                            Payment History
                          </button>
                        </>
                      ) : (
                        <span className="profile-muted">
                          Only the app owner / super-admin can change the plan.
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── 3. Session ── */}
          <section className="profile-card profile-signout-card">
            <div>
              <h2 className="profile-section-title" style={{ marginBottom: 4 }}>Session</h2>
              <p className="profile-muted">Signed in as {email}</p>
            </div>
            <button onClick={handleSignOut} className="btn-secondary">Log Out</button>
          </section>
        </div>
      </main>

      {/* ── Upgrade / change plan modal ── */}
      {upgradeFor && (
        <div className="modal-backdrop" onClick={() => { setUpgradeFor(null); setUpgradeError(null); }}>
          <div
            className="modal-panel modal-panel--lg"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: Math.min(900, 260 * (data?.availablePlans?.length || 1) + 120) }}
          >
            <div className="modal-header">
              <div>
                <h2>Choose A Plan For {upgradeFor.app_name}</h2>
                <p className="profile-modal-subtitle">{upgradeFor.user_count || 0} active user{upgradeFor.user_count === 1 ? '' : 's'}</p>
              </div>
              <button className="modal-close" onClick={() => { setUpgradeFor(null); setUpgradeError(null); }}>×</button>
            </div>
            <div className="modal-body">
              {upgradeError && <div className="profile-banner error" style={{ marginBottom: 16 }}>{upgradeError}</div>}
              <div className="profile-plans-grid">
                {data?.availablePlans?.map((p) => {
                  const isCurrent = isPlanCurrent(upgradeFor.plan, p);
                  let features = {};
                  try { features = JSON.parse(p.features_json || '{}'); } catch { features = {}; }
                  return (
                    <div
                      key={p.id}
                      className={`profile-plan-card ${isCurrent ? 'is-current' : ''}`}
                    >
                      <div className="profile-plan-name">{p.name}</div>
                      <div className="profile-plan-price">
                        <div>{paiseToINR(p.price_paise)} / {p.interval} per user</div>
                        {(() => {
                          const gst = calculateGstBreakdown(p.price_paise, p.gst_percentage, upgradeFor.user_count || 1);
                          return (
                            <div className="profile-plan-total">
                              {gst.gstPercentage > 0 ? (
                                <>
                                  {paiseToINR(gst.basePaise)} + {paiseToINR(gst.gstAmountPaise)} GST ({gst.gstPercentage}%) = {paiseToINR(gst.totalPaise)}
                                </>
                              ) : (
                                <>Total: {paiseToINR(gst.totalPaise)}</>
                              )}
                              {' '}({upgradeFor.user_count || 0} user{upgradeFor.user_count === 1 ? '' : 's'})
                            </div>
                          );
                        })()}
                      </div>
                      <ul className="profile-plan-features">
                        <li>
                          {isCurrent && p.price_paise > 0 && upgradeFor.purchased_seats != null
                            ? `Up to ${upgradeFor.purchased_seats} users (purchased)`
                            : p.max_users != null ? `Up to ${p.max_users} users` : 'Unlimited users'}
                        </li>
                        {Object.entries(features).map(([k, v]) => {
                          if (typeof v === 'boolean') {
                            return v ? <li key={k}>✓ {k.replace(/_/g, ' ')}</li> : null;
                          }
                          return <li key={k}>{k.replace(/_/g, ' ')}: {String(v)}</li>;
                        })}
                      </ul>
                      {isCurrent ? (
                        <div className="profile-plan-current">Current Plan</div>
                      ) : p.price_paise === 0 ? (
                        <div className="profile-plan-current profile-muted">
                          Free Plan — Assigned By Platform Admin
                        </div>
                      ) : (
                        <button
                          className="btn-primary"
                          disabled={checkoutBusy}
                          onClick={() => {
                            setSeatCountValue(String(upgradeFor.user_count || 1));
                            setSeatCountFor({ app: upgradeFor, plan: p });
                          }}
                        >
                          {checkoutPlanId === p.id ? 'Opening Checkout…' : 'Pay & Switch'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Seat count entry, before handing off to Razorpay ── */}
      {seatCountFor && (
        <div className="modal-backdrop" onClick={() => setSeatCountFor(null)}>
          <div className="modal-panel modal-panel--sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>How Many Members?</h2>
              <button className="modal-close" onClick={() => setSeatCountFor(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
                {seatCountFor.plan.name} is {paiseToINR(seatCountFor.plan.price_paise)} / {seatCountFor.plan.interval} per member.
                Enter how many members this plan should cover — it must be at least your current{' '}
                {seatCountFor.app.user_count || 0} active member(s).
              </p>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                Number of Members
              </label>
              <input
                type="number"
                min={seatCountFor.app.user_count || 1}
                max={MAX_SEAT_COUNT}
                value={seatCountValue}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                  if (digitsOnly === '') { setSeatCountValue(''); return; }
                  const clamped = Math.min(Number(digitsOnly), MAX_SEAT_COUNT);
                  setSeatCountValue(String(clamped));
                }}
                style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', background: 'var(--bg-input)' }}
              />
              <div style={{ marginTop: 10, fontWeight: 600, color: 'var(--text-primary)' }}>
                {(() => {
                  const gst = calculateGstBreakdown(seatCountFor.plan.price_paise, seatCountFor.plan.gst_percentage, Number(seatCountValue) || 0);
                  return gst.gstPercentage > 0 ? (
                    <>{paiseToINR(gst.basePaise)} + {paiseToINR(gst.gstAmountPaise)} GST ({gst.gstPercentage}%) = {paiseToINR(gst.totalPaise)} / {seatCountFor.plan.interval}</>
                  ) : (
                    <>Total: {paiseToINR(gst.totalPaise)} / {seatCountFor.plan.interval}</>
                  );
                })()}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary" onClick={() => setSeatCountFor(null)}>Cancel</button>
                <button
                  className="btn-primary"
                  disabled={
                    !Number.isInteger(Number(seatCountValue)) ||
                    Number(seatCountValue) < (seatCountFor.app.user_count || 1) ||
                    Number(seatCountValue) > MAX_SEAT_COUNT
                  }
                  onClick={() => {
                    const { app, plan } = seatCountFor;
                    const count = Number(seatCountValue);
                    setSeatCountFor(null);
                    startCheckout(app, plan, count);
                  }}
                >
                  Continue to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Payment history modal ── */}
      {paymentsFor && (
        <div className="modal-backdrop" onClick={() => setPaymentsFor(null)}>
          <div
            className="modal-panel modal-panel--lg"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 800 }}
          >
            <div className="modal-header">
              <h2>Payment History — {paymentsFor.app_name}</h2>
              <button className="modal-close" onClick={() => setPaymentsFor(null)}>×</button>
            </div>
            <div className="modal-body">
              {paymentsList.length === 0 ? (
                <div className="profile-empty">No payments yet.</div>
              ) : (
                <table className="profile-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Base</th><th>GST</th><th>Total</th><th>Status</th>
                      <th>Order ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsList.map((p) => (
                      <tr key={p.id}>
                        <td>{formatDate(p.created_at)}</td>
                        <td>{p.base_amount_paise != null ? paiseToINR(p.base_amount_paise) : '—'}</td>
                        <td>{p.gst_amount_paise != null ? `${paiseToINR(p.gst_amount_paise)} (${p.gst_percentage}%)` : '—'}</td>
                        <td>{paiseToINR(p.total_amount_paise ?? p.amount_paise)}</td>
                        <td><StatusBadge status={displayPaymentStatus(p)} /></td>
                        <td><code style={{ fontSize: 11 }}>{p.razorpay_order_id}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Small alert modal for page-level errors/notices (payment status, etc.) ── */}
      {(error || info) && (
        <div className="modal-backdrop" onClick={() => { setError(null); setInfo(null); }}>
          <div className="modal-panel modal-panel--sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{error ? 'Something Went Wrong' : 'Notice'}</h2>
              <button className="modal-close" onClick={() => { setError(null); setInfo(null); }}>×</button>
            </div>
            <div className="modal-body">
              <div className={`profile-banner ${error ? 'error' : 'profile-banner-info'}`}>
                {error || info}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button className="btn-primary" onClick={() => { setError(null); setInfo(null); }}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
